import { useEffect, useRef, useState } from 'react'
import type {
  ComponentProgressDetailView,
  LessonProcessDetailView,
} from '@/types/coaching'

function bySortOrder(
  a: ComponentProgressDetailView,
  b: ComponentProgressDetailView,
) {
  return a.sortOrder - b.sortOrder
}

export function pickDefaultUnitId(
  lesson: LessonProcessDetailView | null,
): number | null {
  if (!lesson || lesson.components.length === 0) return null
  const sorted = [...lesson.components].sort(bySortOrder)

  const firstInProgress = sorted.find((c) => c.status === 'in_progress')
  if (firstInProgress) return firstInProgress.componentProgressId

  const completed = sorted.filter((c) => c.status === 'completed')
  if (completed.length > 0) {
    return completed[completed.length - 1].componentProgressId
  }

  return sorted[0].componentProgressId
}

/**
 * Owns the focus-unit selection inside the focused lesson.
 * Resets to the default each time the lesson identity changes.
 */
export function useFocusUnit(lesson: LessonProcessDetailView | null) {
  const [focusUnitId, setFocusUnitId] = useState<number | null>(() =>
    pickDefaultUnitId(lesson),
  )
  const prevLessonIdRef = useRef<number | null>(lesson?.lessonId ?? null)

  useEffect(() => {
    const nextLessonId = lesson?.lessonId ?? null

    setFocusUnitId((current) => {
      const lessonChanged = prevLessonIdRef.current !== nextLessonId
      prevLessonIdRef.current = nextLessonId

      if (lessonChanged) return pickDefaultUnitId(lesson)
      if (current != null && lesson?.components.some((c) => c.componentProgressId === current)) {
        return current
      }
      return pickDefaultUnitId(lesson)
    })
  }, [lesson])

  return { focusUnitId, setFocusUnitId }
}
